/**
 * useReadBack (T-1g-READ) — the shell data hook for the two server-backed
 * tabs, Decisions and the Action List.
 *
 * It composes the three pieces the read path needs and hands the tab ONE list
 * it can render without further thought:
 *
 *   server read (read-client)  →  last-known cache (read-cache)  →  reconcile
 *   with in-flight local changes (reconcile)                     →  items
 *
 * Design commitments, all of which came from real defects in this app:
 *
 *  * **A failed read never empties a tab.** It falls back to the cached view
 *    and reports `stale`. Rendering `[]` because the radio dropped would tell a
 *    super they have no open decisions — the worst lie this product can tell.
 *  * **A local change is never visually undone.** Every render reconciles the
 *    server view against pending outbox records, so a resolve made with no
 *    signal stays resolved on screen.
 *  * **Re-reads are store-driven, not call-site-driven.** It subscribes to the
 *    outbox change signal for the same reason `useOutboxRecords` does: the one
 *    caller that forgets to refresh is always the important one.
 *  * **Project-scoped.** Switching projects clears immediately rather than
 *    showing another job's list while the new read is in flight.
 */

import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { onOutboxChanged } from "../outbox/changes";
import { readCacheGet, readCachePut, type ReadSurface } from "../read/read-cache";
import type { ReadClient, ReadOutcome } from "../read/read-client";
import type { OutboxRecord } from "../outbox/types";

export interface ReadBackState<T> {
  /** Unreconciled server/cache rows. Mutation authority must compare these with
   * retained local carriers rather than deriving truth from the display list. */
  authoritativeItems: T[];
  items: T[];
  /** Where `items` came from — drives the honest freshness line in the UI. */
  source: "server" | "cache" | "none";
  /** Epoch ms of the SERVER view backing `items` (null when never fetched). */
  fetchedAt: number | null;
  /** True while showing a cached view because the latest read failed. */
  stale: boolean;
  /** The failure code of the latest read attempt, if it failed. */
  error: string | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

export interface UseReadBackOptions<T> {
  surface: ReadSurface;
  project_ref: string | null;
  client: ReadClient | null;
  fetcher: (client: ReadClient) => Promise<ReadOutcome<T>>;
  /** Outbox records to reconcile against (the shell already holds these). */
  records: OutboxRecord[];
  reconcile: (items: T[], records: OutboxRecord[], fetchedAt: number | null) => T[];
}

export function useReadBack<T>(opts: UseReadBackOptions<T>): ReadBackState<T> {
  const { surface, project_ref, client, fetcher, records, reconcile } = opts;
  const [serverItems, setServerItems] = useState<T[]>([]);
  const [source, setSource] = useState<"server" | "cache" | "none">("none");
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // The fetcher is held in a ref rather than a dependency. A caller passing an
  // inline arrow (the obvious way to write the call site) would otherwise give
  // `refresh` a new identity every render, re-run the effect, set state, and
  // spin forever. Making the hook immune to that is the hook's job, not a
  // memoization rule every call site has to remember.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // Generation counter: only the NEWEST refresh may write state.
  //
  // Refreshes are store-driven and therefore concurrent — committing one capture
  // emits four outbox writes (held → released → sending → delivered), each
  // firing its own refresh, and nothing cancels the earlier ones. Their
  // responses can land in any order, so a read started BEFORE the server had the
  // row could resolve AFTER the post-delivery read and overwrite it with a view
  // that predates the write.
  //
  // That is the "I had to pull-to-refresh before my Action showed up" defect
  // (Red, 2026-07-28): the data was committed server-side the whole time, the
  // post-delivery read did fetch it, and a slower in-flight read from one state
  // transition earlier clobbered the result. A manual refresh appeared to fix it
  // only because it had no competitor still in flight.
  const genRef = useRef(0);
  /** Which project the rows in state belong to (see refresh). */
  const shownForRef = useRef<string | null>(null);

  const refresh = useCallback(async () => {
    const gen = ++genRef.current;
    /** False once a newer refresh has started — this one must not write. */
    const current = (): boolean => gen === genRef.current;

    // The project the rows currently in state belong to. The null-project branch
    // below already refuses to show "the previous job's list", but a switch
    // between two REAL projects fell straight through it: the cache seed only
    // calls setServerItems when a cache entry EXISTS, so moving to a project with
    // no cache left the previous one's rows on screen under the new label.
    //
    // That was cosmetic while everyone shared one scope. With per-person scopes
    // it reads as a project you are not allowed to open showing you data anyway —
    // caught live on 2026-07-30, when a Fray-only actor switched to Foster and saw
    // 69 rows that were Fray's.
    if (shownForRef.current !== project_ref) {
      shownForRef.current = project_ref;
      setServerItems([]);
      setFetchedAt(null);
      setError(null);
    }

    if (project_ref === null) {
      // No project selected: show nothing rather than the previous job's list.
      setServerItems([]);
      setSource("none");
      setFetchedAt(null);
      setError(null);
      return;
    }

    // Seed from cache FIRST so a cold launch on a dead signal paints the
    // last-known list immediately instead of an empty tab that then fills in.
    const cached = await readCacheGet<T>(surface, project_ref);
    // The cache seed is guarded too: an older refresh reaching this line late
    // would paint the last-known list over a fresher server view.
    if (cached && current()) {
      setServerItems(cached.items);
      setFetchedAt(cached.fetchedAt);
      setSource("cache");
    }

    if (client === null) {
      // No endpoint baked in — cache-only. Not an error the super caused, and
      // not one they can fix, so it is not surfaced as a failure.
      if (!cached) {
        setServerItems([]);
        setSource("none");
      }
      return;
    }

    setLoading(true);
    const outcome = await fetcherRef.current(client);
    if (!current()) return; // superseded mid-flight: do not touch state at all
    setLoading(false);

    if (outcome.ok) {
      const now = Date.now();
      setServerItems(outcome.items);
      setFetchedAt(now);
      setSource("server");
      setError(null);
      // The cache write is INSIDE the guard: a superseded read must not leave a
      // stale view behind for the next cold launch either.
      await readCachePut(surface, project_ref, outcome.items, now);
      return;
    }
    // A REFUSAL is not a network blip. `offline` or a 5xx means "we could not
    // ask", and keeping the last-known list is exactly right for a super on a
    // dead signal. Being refused means the server answered and said no — this
    // person may not see this project — so continuing to show rows would present
    // data under a project they cannot open.
    if (outcome.error === "project_out_of_scope" || outcome.error === "invalid_token") {
      setServerItems([]);
      setFetchedAt(null);
      setSource("none");
    }
    // Failed: keep whatever we are showing (cached or previous) and say so.
    setError(outcome.error);
    // Functional update so `source` is not a dependency of `refresh` — reading
    // state that `refresh` also writes would re-create it on every change and
    // re-trigger the effect that calls it.
    setSource((prev) => (cached ? "cache" : prev === "server" ? "server" : "none"));
    // NOTE: `fetchedAt` is deliberately NOT advanced on failure — it timestamps
    // the data, not the attempt, so the UI cannot imply freshness it lacks.
  }, [surface, project_ref, client]);

  useEffect(() => {
    void refresh();
    // Re-read when the store changes: a resolve/status change just landed, and
    // the server view it affects should be re-pulled rather than waiting for a
    // wake event.
    const unsubscribe = onOutboxChanged(() => void refresh());
    if (typeof window === "undefined") return unsubscribe;

    const wake = (): void => void refresh();
    const onVisibility = (): void => {
      if (document.visibilityState === "visible") void refresh();
    };
    window.addEventListener("online", wake);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      unsubscribe();
      window.removeEventListener("online", wake);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // `refresh` closes over project_ref/client; re-subscribing on change is
    // intended (a project switch must re-read).
  }, [refresh]);

  return {
    authoritativeItems: serverItems,
    // Reconciliation happens on every render, not at fetch time: the outbox can
    // change between reads, and a pending local change must win immediately.
    items: reconcile(serverItems, records, fetchedAt),
    source,
    fetchedAt,
    stale: source === "cache" && error !== null,
    error,
    loading,
    refresh,
  };
}
